import { useRef } from "react"
import type { BaseSyntheticEvent, ComponentProps, FC, ReactElement, RefObject } from "react"

import map from "lodash-es/map"
import { useTranslation } from "react-i18next"
import { useClickAway, useKeyPressEvent, useToggle } from "react-use"

import { Avatar } from "@/3514/components/shared/avatar/avatar.component"
import { EAvatarSize } from "@/3514/components/shared/avatar/avatar.types"
import { Button } from "@/3514/components/shared/button/button.component"
import { Link } from "@/3514/components/shared/link/link.component"
import { type TScreenBreakpoints, useIsFakeParticipant } from "@/3514/hooks"
// Deep, not the page barrel. A component has no business pulling a page in the first place, and
// that barrel also re-exports the onboarding pages and ui.hooks, which reach
// participant.coaching-sessions and from there back into this component tree - so the whole graph
// was entering initialisation here (`(0, utils_1.getCssIdentifierFromFileName) is not a function`).
import { ParticipantOnboardingStepper as OnboardingStepper } from "@/3514/pages/participant.onboarding/participant.onboarding.stepper.component"
import { type IAppState, type TAppDispatcher, useAppDispatcher, useAppSelector } from "@/3514/store/app"
import type { IProductTypeModel } from "@/3514/types"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, type TTFunction, type TUseLogoNavigation, useLogoNavigation } from "@/hooks"
import ProductTypeLogo from "@/shared/ProductTypeLogo"
import { HeaderDropdownProfileList } from "@/shared/header-dropdown-profile-list"
import type { TEmptyCallback } from "@/shared/types/functions"

import { participantHeaderConfig as config } from "./participant.header.config"
import { participantHeaderStyles as classes } from "./participant.header.styles"
import { EParticipantHeaderVariant as EVariant } from "./participant.header.types"

const { renderLinks } = config

function _useDesktopDropdown(): {
    t: TTFunction
    isFakeParticipant: boolean
    isDropdownOpen: boolean
    dropdownRef: RefObject<HTMLDivElement>
    openDropdown: TEmptyCallback
    closeDropdown: TEmptyCallback
} {
    const { t }: IUseTranslation = useTranslation()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const [isDropdownOpen, setIsDropdownOpen]: ReturnType<typeof useToggle> = useToggle(false)

    const dropdownRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const { isOpenBurgerMenu }: IAppState = useAppSelector()

    const { toggleBurgerMenu }: TAppDispatcher = useAppDispatcher()

    const openDropdown: TEmptyCallback = (): void => setIsDropdownOpen(true)

    const closeDropdown: TEmptyCallback = (): void => setIsDropdownOpen(false)

    useClickAway(dropdownRef, (): void => setIsDropdownOpen(false))

    useKeyPressEvent("Escape", (): void => {
        if (isOpenBurgerMenu) toggleBurgerMenu()

        setIsDropdownOpen(false)
    })

    return { t, isFakeParticipant, isDropdownOpen, dropdownRef, openDropdown, closeDropdown }
}

const _DropdownTrigger: FC<{
    ariaLabel: string
    isDropdownOpen: boolean
    isFakeParticipant: boolean
    isRelative?: boolean
    avatarSize?: EAvatarSize
    onOpen(): void
    className?: string
}> = ({
    ariaLabel,
    isDropdownOpen,
    isFakeParticipant,
    isRelative = false,
    onOpen,
    avatarSize = EAvatarSize._25,
    className = String("")
}): ReactElement => (
    <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isDropdownOpen}
        onClick={withPressHandler(onOpen)}
        className={buildCssClassName(
            classes.desktop.dropdownOpener({
                isDropdownOpen,
                isFakeParticipant,
                isRelative
            }),
            { [className]: className }
        )}
    >
        <Avatar size={avatarSize} isCurrentUser isAccessible />
    </button>
)

const _MobileChildrenDefault: FC = (): ReactElement => {
    const { handleLogoNavigation }: TUseLogoNavigation = useLogoNavigation()

    const { isOpenBurgerMenu }: IAppState = useAppSelector()

    const { toggleBurgerMenu }: TAppDispatcher = useAppDispatcher()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    return (
        <div
            className={classes.mobile.container({
                isFakeParticipant,
                isOpenBurgerMenu
            })}
        >
            {isOpenBurgerMenu || (
                <ProductTypeLogo handleLogoClick={handleLogoNavigation} className={classes.mobile.logo()} />
            )}

            <Button
                isContainer
                aria-expanded={isOpenBurgerMenu}
                aria-label="Toggle menu"
                onClick={(): void => toggleBurgerMenu()}
                className={classes.mobile.button()}
            >
                <span className={classes.mobile.buttonLine1({ isOpenBurgerMenu, isAccent: true })} />
                <span className={classes.mobile.buttonLine2({ isOpenBurgerMenu, isAccent: true })} />
                <span className={classes.mobile.buttonLine3({ isOpenBurgerMenu, isAccent: true })} />
            </Button>
        </div>
    )
}

const _MobileOnboardingDropdownList: FC<{
    isDropdownOpen: boolean
    isFakeParticipant: boolean
    linksProps: ComponentProps<typeof Link>[]
    onClose: TEmptyCallback
}> = ({ isDropdownOpen, isFakeParticipant, linksProps, onClose }): ReactElement => {
    return (
        <div
            className={classes.mobileOnboarding.dropdownListContainer({
                isFakeParticipant,
                isDropdownOpen
            })}
        >
            <div className={classes.mobileOnboarding.topContainer()}>
                <div className={classes.mobileOnboarding.triggerWrapper()}>
                    <Button
                        isContainer
                        aria-expanded
                        aria-label="Close menu"
                        onClick={onClose}
                        className={classes.mobile.button()}
                    >
                        <span className={classes.mobile.buttonLine1({ isOpenBurgerMenu: true, isAccent: false })} />
                        <span className={classes.mobile.buttonLine2({ isOpenBurgerMenu: true, isAccent: false })} />
                        <span className={classes.mobile.buttonLine3({ isOpenBurgerMenu: true, isAccent: false })} />
                    </Button>
                </div>
            </div>

            <div className={classes.mobileOnboarding.bottomContainer()}>
                <Avatar
                    size={EAvatarSize._35}
                    isCurrentUser
                    isAccessible
                    className={classes.mobileOnboarding.avatar()}
                />

                {map<ComponentProps<typeof Link>, ReactElement>(
                    linksProps,
                    (props: ComponentProps<typeof Link>): ReactElement => (
                        <Link {...props} key={`phl-${props.to}`} />
                    )
                )}

                {isFakeParticipant ? (
                    <HeaderDropdownProfileList
                        listClassName={classes.desktop.profileList({
                            isMobile: false
                        })}
                        titleClassName={classes.desktop.profileListTitle({
                            isMobile: false
                        })}
                    />
                ) : null}
            </div>
        </div>
    )
}

const _MobileChildrenOnboarding: FC<{
    linksProps: ComponentProps<typeof Link>[]
}> = ({ linksProps }): ReactElement => {
    const { handleLogoNavigation }: TUseLogoNavigation = useLogoNavigation()

    const { t, isFakeParticipant, isDropdownOpen, openDropdown, closeDropdown } = _useDesktopDropdown()

    return (
        <div className={classes.mobileOnboarding.container()}>
            {isDropdownOpen || (
                <ProductTypeLogo handleLogoClick={handleLogoNavigation} className={classes.mobileOnboarding.logo()} />
            )}

            <OnboardingStepper />

            <_DropdownTrigger
                ariaLabel={t("participantSide.header.dropdown.opener.ariaLabel")}
                isDropdownOpen={isDropdownOpen}
                isFakeParticipant={isFakeParticipant}
                onOpen={openDropdown}
                avatarSize={EAvatarSize._35}
                className="!top-[-5px] right-0 !relative"
            />

            <_MobileOnboardingDropdownList
                isDropdownOpen={isDropdownOpen}
                onClose={closeDropdown}
                isFakeParticipant={isFakeParticipant}
                linksProps={linksProps}
            />
        </div>
    )
}

const _DesktopDropdownList: FC<{
    dropdownRef: RefObject<HTMLDivElement>
    isDropdownOpen: boolean
    isFakeParticipant: boolean
    isTriggerRelative?: boolean
    linksProps: ComponentProps<typeof Link>[]
}> = ({ dropdownRef, isDropdownOpen, isFakeParticipant, linksProps, isTriggerRelative = false }): ReactElement => (
    <div
        className={classes.desktop.list({
            isDropdownOpen,
            isFakeParticipant,
            isTriggerRelative
        })}
        ref={dropdownRef}
    >
        {map<ComponentProps<typeof Link>, ReactElement>(
            linksProps,
            (props: ComponentProps<typeof Link>): ReactElement => (
                <Link {...props} key={`phl-${props.to}`} />
            )
        )}

        {isFakeParticipant ? (
            <HeaderDropdownProfileList
                listClassName={classes.desktop.profileList({
                    isMobile: false
                })}
                titleClassName={classes.desktop.profileListTitle({
                    isMobile: false
                })}
            />
        ) : null}
    </div>
)

const _DesktopChildrenDefault: FC<{ linksProps: ComponentProps<typeof Link>[] }> = ({ linksProps }): ReactElement => {
    const { t, isFakeParticipant, isDropdownOpen, dropdownRef, openDropdown } = _useDesktopDropdown()

    return (
        <div className={classes.desktop.container({ isDropdownOpen })}>
            <_DropdownTrigger
                ariaLabel={t("participantSide.header.dropdown.opener.ariaLabel")}
                isDropdownOpen={isDropdownOpen}
                isFakeParticipant={isFakeParticipant}
                onOpen={openDropdown}
            />

            <_DesktopDropdownList
                dropdownRef={dropdownRef}
                isDropdownOpen={isDropdownOpen}
                isFakeParticipant={isFakeParticipant}
                linksProps={linksProps}
            />
        </div>
    )
}

const _DesktopChildrenOnboarding: FC<{ linksProps: ComponentProps<typeof Link>[] }> = ({
    linksProps
}): ReactElement => {
    const { t, isFakeParticipant, isDropdownOpen, dropdownRef, openDropdown } = _useDesktopDropdown()

    return (
        <div className={classes.desktop.onboardingContainer()}>
            <ProductTypeLogo className={classes.desktop.wiserLogo()} />

            <OnboardingStepper />

            <_DropdownTrigger
                ariaLabel={t("participantSide.header.dropdown.opener.ariaLabel")}
                isDropdownOpen={isDropdownOpen}
                isFakeParticipant={isFakeParticipant}
                onOpen={openDropdown}
                isRelative
            />

            <_DesktopDropdownList
                dropdownRef={dropdownRef}
                isDropdownOpen={isDropdownOpen}
                isFakeParticipant={isFakeParticipant}
                linksProps={linksProps}
                isTriggerRelative
            />
        </div>
    )
}

function renderMobileChildren({
    variant,
    breakpoints,
    t,
    handleLogout,
    productType
}: {
    variant: EVariant
    breakpoints: TScreenBreakpoints
    t: TTFunction
    handleLogout(e: BaseSyntheticEvent): void
    productType: IProductTypeModel
}): ReactElement {
    const links: { [V in EVariant]: ComponentProps<typeof Link>[] } = renderLinks({
        breakpoints,
        t,
        handleLogout,
        productType
    })

    switch (variant) {
        case EVariant.Onboarding:
            return <_MobileChildrenOnboarding linksProps={links[variant]} />
        case EVariant.Default:
        default:
            return <_MobileChildrenDefault />
    }
}

function renderDesktopChildren({
    variant,
    breakpoints,
    t,
    handleLogout,
    productType
}: {
    variant: EVariant
    breakpoints: TScreenBreakpoints
    t: TTFunction
    handleLogout(e: BaseSyntheticEvent): void
    productType: IProductTypeModel
}): ReactElement {
    const links: { [V in EVariant]: ComponentProps<typeof Link>[] } = renderLinks({
        breakpoints,
        t,
        handleLogout,
        productType
    })

    switch (variant) {
        case EVariant.Onboarding:
            return <_DesktopChildrenOnboarding linksProps={links[variant]} />
        case EVariant.Default:
        default:
            return <_DesktopChildrenDefault linksProps={links[variant]} />
    }
}

const elements = { renderMobileChildren, renderDesktopChildren }

export { elements as participantHeaderElements }
